import React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';

import i18nCoordinator from './i18nCoordinator/i18nCoordinator';
import i18nMoment from './libs/i18nMoment';
import i18nMoney from './libs/i18nMoney';
import i18nNumber from './libs/i18nNumber';
import { i18next as i18nSingleton } from './libs/i18next';

let _i18nInstance = i18nSingleton;
let DEFAULT_PROVIDES = {
    i18nMoment,
    i18nMoney,
    i18nNumber,
    t: i18nSingleton.makeTfn(),
};

function init(i18nInstance) {
    _i18nInstance = i18nInstance;
    DEFAULT_PROVIDES.t = _i18nInstance.makeTfn();
}

/**
 * @param {React.Component | React.FC<Props>} WrappedComponentClass
 * @param {Array<String>} opts - an optional subset of localize functions to include
 * @param {String} namespace - the default namespace of t-function strings
 * @return {React.ComponentType<Props>}
 */
// eslint-disable-next-line default-param-last
function localize(WrappedComponentClass, opts = [], namespace) {
    let ns = namespace;
    let provides;

    function buildProvides() {
        provides = {
            ...DEFAULT_PROVIDES,
        };

        if (typeof opts === 'string' && namespace === undefined) {
            ns = opts;
        } else if (Array.isArray(opts) && !!opts.length) {
            provides = opts.reduce((acc, key) => {
                return {
                    ...acc,
                    [key]: DEFAULT_PROVIDES[key],
                };
            }, {});
        }

        if (ns) {
            const t = _i18nInstance.makeTfn(ns);
            provides.t = t;
        }
    }

    const componentName = WrappedComponentClass.displayName
        || WrappedComponentClass.name
        || 'UnnamedComponent';

    class Localized extends React.PureComponent {
        static displayName = `Localize(${componentName})`;

        constructor(props) {
            super(props);
            buildProvides();
        }

        onLocaleChange = (locale) => {
            provides.locale = locale;

            if (provides.t && ns) {
                provides.t = _i18nInstance.makeTfn(ns);
            }

            this.forceUpdate();
        };

        componentDidMount() {
            // Right now LocaleCoordinator triggers the locale change listeners
            // on translation resource addition for the benefit of this function
            // Eventually we should be refactor to listen to changes
            // in resources over locale. We can also optimize by listening to
            // only the changes in the specified namespace, but right now components
            // can use t('randomNamespace:foo') which makes the optimization impossible
            // We should also restrict the usage of the t() function so this isn't
            // allowed

            this.handleCurrency = i18nCoordinator
                .addCurrencyChangeListener(() => this.forceUpdate());
            this.handleLocale = i18nCoordinator
                .addLocaleChangeListener(this.onLocaleChange);
        }

        componentWillUnmount() {
            i18nCoordinator.removeCurrencyChangeListener(this.handleCurrency);
            i18nCoordinator.removeLocaleChangeListener(this.handleLocale);
        }

        render() {
            return (
                <WrappedComponentClass
                    {...this.props}
                    {...provides}
                />
            );
        }
    }

    return hoistNonReactStatics(Localized, WrappedComponentClass);
}

export {
    init,
};
export default localize;
